<script setup lang="ts">
/**
 * OTC 문서 내 [[변수]] 자리 — CddDocVarField 미러링.
 *   · combo(nationality/occupation/race/subscriber type) → 인라인 콤보박스
 *   · date  → 인라인 편집 + 📅 달력 아이콘
 *   · person → 인라인 편집 + ✨ (챗봇 person lookup)
 *   · address → 인라인 편집 + 📍 (주소 입력)
 *   · text  → 인라인 직접 편집
 *   · 드래그앤드롭 / pick-place 지원
 */
import { computed, inject, ref, onMounted, watch, nextTick } from 'vue';
import { OTC_DOC_CTX } from './otcContext';
import { subStoreKey } from './otcFields';

const props = defineProps<{ fieldKey: string; placeholder?: string; index?: number }>();
const ctx = inject(OTC_DOC_CTX)!;

// Subscription 등 allottee 별 렌더 시 index 를 주면 값은 해당 allottee 저장키로, 타입/라벨/콤보는 base 키로.
const storeKey = computed(() => (props.index != null ? subStoreKey(props.fieldKey, props.index) : props.fieldKey));

const value = computed(() => ctx.valueOf(storeKey.value));
const label = computed(() => props.placeholder || ctx.labelOf(props.fieldKey));
const fieldType = computed(() => ctx.typeOf(props.fieldKey));
const isDate = computed(() => fieldType.value === 'date');
const isAddress = computed(() => fieldType.value === 'address');
const isCombo = computed(() => fieldType.value === 'combo');
const isFreeCombo = computed(() => fieldType.value === 'combo-free'); // 목록 선택 + 직접입력(v-combobox)
// 파생(읽기전용) 필드 — 상단/합계/배정입력에서 자동 결정되므로 문서에서 직접 편집 불가.
//   · total_new_shares : 개별 배정 수량의 합
//   · subscriber_type  : 상단 Subscriber Type 토글(Individual/Corporate)을 그대로 따름
//   · Section 78(TMP084) 명세 primary 행 · 요약표 : 배정 수량 × 주가에서 자동 계산
const DERIVED_KEYS = new Set([
  'total_new_shares', 'subscriber_type',
  'share_class_number_1', 'share_class_price_1', 'share_class_total_1',
  'total_current_cash', 'total_additional_cash', 'total_accumulated_cash',
  'total_current_otherwise', 'total_additional_otherwise', 'total_accumulated_otherwise',
  'total_current', 'total_additional', 'total_accumulated',
]);
const isDerived = computed(() => DERIVED_KEYS.has(props.fieldKey));
const showIcon = computed(() => ['person', 'entity', 'date', 'address'].includes(fieldType.value));
const active = computed(() => ctx.activeKey.value === storeKey.value);
const invalid = computed(() => (ctx.isInvalid ? ctx.isInvalid(storeKey.value) : false));

function onIcon() {
  if (isAddress.value) ctx.openAddress(storeKey.value);
  else ctx.activate(storeKey.value);
}
const iconName = computed(() => (isDate.value ? 'mdi-calendar-month' : isAddress.value ? 'mdi-map-marker-outline' : 'mdi-creation'));
const iconClass = computed(() => (isDate.value ? 'ic-date' : isAddress.value ? 'ic-addr' : 'ic-ai'));
const iconTip = computed(() => (isDate.value ? 'Pick a date' : isAddress.value ? 'Enter address' : 'Look up from chat'));

const comboItems = computed(() => ctx.comboOptionsOf(props.fieldKey));
function onCombo(v: any) { ctx.setFieldValue(storeKey.value, v ? String(v) : ''); }
// v-combobox(자유입력) — 선택 시 item 객체/문자열 모두 올 수 있어 값만 추출.
function onComboFree(v: any) {
  const val = v == null ? '' : (typeof v === 'object' ? String(v.value ?? v.label ?? '') : String(v));
  ctx.setFieldValue(storeKey.value, val);
}

// ── 인라인 편집 (combo 아닌 경우) ──
const ed = ref<HTMLElement>();
onMounted(() => { if (ed.value) ed.value.textContent = value.value; });
watch(value, async (v) => {
  await nextTick();
  if (ed.value && document.activeElement !== ed.value && ed.value.textContent !== v) ed.value.textContent = v;
});
function onInput() {
  if (!ed.value) return;
  const txt = ed.value.textContent || '';
  if (!txt && ed.value.innerHTML) ed.value.innerHTML = '';
  ctx.setFieldValue(storeKey.value, txt);
}
function clearField() {
  ctx.setFieldValue(storeKey.value, '');
  if (ed.value) ed.value.innerHTML = '';
}

// ── 드래그앤드롭 ──
const dragOver = ref(false);
function onDrop(e: DragEvent) {
  dragOver.value = false;
  let v = '';
  const json = e.dataTransfer?.getData('application/x-entity-field');
  if (json) { try { v = String(JSON.parse(json).value ?? ''); } catch { /* noop */ } }
  if (!v) v = e.dataTransfer?.getData('text/plain') || '';
  if (v) ctx.setFieldValue(storeKey.value, v.trim());
}

// ── pick-place ──
const placeable = computed(() => !!ctx.pendingPickValue.value);
function onFieldMousedown(e: MouseEvent) {
  if (!ctx.pendingPickValue.value) return;
  e.preventDefault();
  ctx.placePick(storeKey.value);
}
</script>

<template>
  <!-- 파생 읽기전용(Total = Σ 개별 배정 수량) — 편집 불가, 자동 계산값만 표시 -->
  <span
    v-if="isDerived"
    :data-otc-field="storeKey"
    class="otcf otcf-derived" :class="{ empty: !value, filled: !!value }"
    :title="'Auto-calculated from the allotment quantity / share price entered above'"
  >
    <span class="otcf-in otcf-ro" :data-ph="label">{{ value }}</span>
  </span>

  <v-autocomplete
    v-else-if="isCombo"
    :data-otc-field="storeKey"
    class="otcf-combo" :class="{ filled: !!value, invalid }"
    :model-value="value || null"
    :items="comboItems" item-title="label" item-value="value"
    :placeholder="label" variant="outlined" density="compact" hide-details clearable
    :menu-props="{ maxHeight: 320 }"
    @update:model-value="onCombo"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  />

  <!-- combo-free: 목록 선택 + 직접입력 허용 (v-combobox) -->
  <v-combobox
    v-else-if="isFreeCombo"
    :data-otc-field="storeKey"
    class="otcf-combo otcf-combo-free" :class="{ filled: !!value, invalid }"
    :model-value="value || null"
    :items="comboItems" item-title="label" item-value="value" :return-object="false"
    :placeholder="label" variant="outlined" density="compact" hide-details clearable
    :menu-props="{ maxHeight: 320 }"
    @update:model-value="onComboFree"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  />

  <span
    v-else
    :data-otc-field="storeKey"
    class="otcf" :class="{ empty: !value, filled: !!value, active, dragover: dragOver, placeable, invalid }"
    @mousedown.capture="onFieldMousedown"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  >
    <span
      ref="ed" class="otcf-in" :data-ph="label" contenteditable="true" spellcheck="false"
      @input="onInput" @keydown.enter.prevent="ed?.blur()"
    />
    <button
      v-if="showIcon"
      class="otcf-ic" :class="iconClass" :title="iconTip"
      @mousedown.prevent @click.stop="onIcon"
    >
      <v-icon size="12">{{ iconName }}</v-icon>
    </button>
    <button
      v-if="!!value"
      class="otcf-clear" title="Clear"
      @mousedown.prevent @click.stop="clearField"
    >
      <v-icon size="11">mdi-close</v-icon>
    </button>
  </span>
</template>

<style scoped>
.otcf { display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:0 3px; vertical-align:baseline; }
.otcf.empty { background:#fff6d6; box-shadow:inset 0 -2px 0 #f2c94c; }
.otcf.filled { background:#eaf3ff; box-shadow:inset 0 -2px 0 #1B84FF; }
.otcf.invalid { background:#fdecec; box-shadow:inset 0 -2px 0 #e5484d; }
.otcf.active { outline:2px solid #1B84FF; outline-offset:1px; }
.otcf.dragover { outline:2px dashed #22a06b; background:#e6f7ee; }
.otcf.placeable { outline:1.5px dashed #7bb0ff; cursor:copy; }
.otcf.placeable:hover { background:#eaf3ff; }
.otcf-in { min-width:46px; outline:none; font:inherit; color:#10305c; white-space:pre-wrap; }
.otcf.filled .otcf-in { font-weight:600; }
.otcf-in:empty::before { content:attr(data-ph); color:#9a7b1a; font-style:italic; font-size:.9em; cursor:text; }
.otcf-derived { cursor:default; background:transparent; }
.otcf-ro, .otcf-ro:empty::before { cursor:default; }
.otcf-derived.filled .otcf-ro { font-weight:600; }
.otcf-ic { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; border:none; cursor:pointer; flex:0 0 auto; padding:0; }
.otcf-ic.ic-ai { background:#eaf3ff; color:#1B84FF; }
.otcf-ic.ic-date { background:#eafaf1; color:#22a06b; }
.otcf-ic.ic-addr { background:#fdeefe; color:#a23bb0; }
.otcf-ic:hover { filter:brightness(.94); }
.otcf-clear { display:none; align-items:center; justify-content:center; width:15px; height:15px; border-radius:50%; border:none; background:#c4ccd6; color:#fff; cursor:pointer; flex:0 0 auto; padding:0; }
.otcf:hover .otcf-clear { display:inline-flex; }
.otcf-clear:hover { background:#8b95a1; }

/* 인라인 콤보박스 — 문서 본문(12px)에 맞춰 컴팩트하게. Vuetify 기본 16px 강제 축소. */
.otcf-combo { display:inline-flex; min-width:180px; max-width:340px; vertical-align:middle; font-size:12px; line-height:1.3; }
.otcf-combo-free { min-width:240px; max-width:560px; }
.otcf-combo :deep(.v-field) { cursor:pointer; border-radius:6px; background:#fff6d6; min-height:26px; font-size:12px; }
.otcf-combo.filled :deep(.v-field) { background:#eaf3ff; }
.otcf-combo.invalid :deep(.v-field) { background:#fdecec; box-shadow:inset 0 -2px 0 #e5484d; }
.otcf-combo :deep(.v-field__field) { min-height:26px; }
.otcf-combo :deep(.v-field__input) { min-height:26px; padding:0 6px; font-size:12px; line-height:1.3; color:#10305c; align-items:center; flex-wrap:nowrap; overflow:visible; }
.otcf-combo :deep(.v-select__selection) { max-width:none; overflow:visible; }
.otcf-combo :deep(.v-field__input),
.otcf-combo :deep(.v-select__selection),
.otcf-combo :deep(.v-select__selection-text),
.otcf-combo :deep(.v-autocomplete__selection-text),
.otcf-combo :deep(input) { font-size:12px !important; line-height:1.3; letter-spacing:normal; }
.otcf-combo.filled :deep(.v-select__selection-text),
.otcf-combo.filled :deep(.v-field__input) { font-weight:600; }
.otcf-combo :deep(input::placeholder) { color:#9a7b1a; font-style:italic; opacity:1; font-size:12px; }
.otcf-combo :deep(.v-field__append-inner) { padding-top:2px; }
.otcf-combo :deep(.v-field__append-inner .v-icon) { font-size:16px; }
.otcf-combo :deep(.v-field__outline) { --v-field-border-opacity:0.25; }
</style>
